import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import '../styles/main.css';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseServices';
import { updateProductClicks } from '../firebase/firebaseitemstats';

function HomeGymPage({ region }) {
  const [cards, setCards] = useState([]);

  useEffect(() => {
    document.title = "Home Gym Equipment & Fitness Essentials | Pure Health Nexus";
    const metaDescription = document.createElement('meta');
    metaDescription.name = "description";
    metaDescription.content = "Create the perfect home gym with top-rated equipment.";
    document.head.appendChild(metaDescription);

    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const itemsRef = collection(db, 'products', 'homegym', 'items');
        const snapshot = await getDocs(itemsRef);

        const fetchedCards = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            image:
              doc.id === 'card0'
                ? data.image
                : region === 'US'
                  ? data.image1
                  : data.image,
            link:
              doc.id === 'card0'
                ? data.link
                : region === 'US'
                  ? data.link1
                  : data.link,
            price:
              doc.id === 'card0'
                ? data.price
                : region === 'US'
                  ? data.price1
                  : data.price,
            text: data.text,
            text1: data.text1,
            title:
              doc.id === 'card0'
                ? data.title
                : region === 'US'
                  ? data.title1
                  : data.title,
            rating:
              doc.id === 'card0'
                ? data.rating
                : region === 'US'
                  ? data.rating1
                  : data.rating,
            review:
              doc.id === 'card0'
                ? data.review
                : region === 'US'
                  ? data.review1
                  : data.review,
            timestamp: region === 'US' ? data.usatimestamp : data.eutimestamp,
            uid: data.uid,
            uid1: data.uid1,
            click: data.click,
            click1: data.click1,
          };
        });

        setCards(fetchedCards);
      } catch (error) {
        console.error('Error fetching data from Firestore:', error);
      }
    };

    fetchData();
  }, [region]);

  useEffect(() => {
    (async () => {
      window.ppms?.tm?.api('setUserId', 'unique-user-id');
      window.ppms?.tm?.api('enableCookies');
      window.ppms?.tm?.api('enableJSErrorTracking', true);
      window.ppms?.tm?.api('trackSiteSearch', 'exerciseequipment', 'searchCategory', 5);
      window.ppms?.tm?.api('trackGoal', 1, 50);
      window.ppms?.tm?.api('trackPageView', 'HomeGymPage', { region });
    })();
  }, [region]);

  // Preload images
  useEffect(() => {
    const preloadImages = (imageUrls) => {
      imageUrls.forEach((url) => {
        const img = new Image();
        img.src = url;
      });
    };

    const imageUrls = cards.map((card) => card.image);
    preloadImages(imageUrls);
  }, [cards]);

  const handleCardClick = (card) => {
    updateProductClicks(card);
    console.log('Card clicked:', card.title);
  };

  const handleImageClick = (card) => {
    console.log('Image clicked:', card.title);
    handleCardClick(card);
    console.log('Tracking image click:', card.title, region);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'Image',
      eventAction: 'Click',
      eventLabel: card.title,
      region: region,
      elementUrl: card.link,
      elementId: card.id,
      elementClasses: 'img-size',
    });
  };

  const handleButtonClick = (card) => {
    console.log('Button clicked:', card.title);
    handleCardClick(card);
    console.log('Tracking button click:', card.title, region);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'Button',
      eventAction: 'Click',
      eventLabel: card.title,
      region: region,
      elementUrl: card.link,
      elementId: card.id,
      elementClasses: 'large-button',
    });
  };

  // Separate out card0 from the rest
  const card0 = cards.find((card) => card.id === 'card0');
  const otherCards = cards.filter((card) => card.id !== 'card0');

  // Commented out to avoid ESLint 'unused' warnings
  // const firstCardWithTimestamp = otherCards.find((card) => card.timestamp);

  // Filter cards updated in the last ~6 months and ensure they have a timestamp
  const now = new Date();
  const maximumAgeInHours = 24 * 365 * 5;

  const filteredCards = otherCards.filter((card) => {
    if (!card.timestamp?.seconds) return false;
    const cardTimestamp = new Date(card.timestamp.seconds * 1000);
    const timeDifference = now - cardTimestamp;
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= maximumAgeInHours;
  });

  // Commented out to avoid ESLint 'unused' warnings
  // const formatTimestamp = (timestamp) => {
  //   if (!timestamp) return 'N/A';
  //   const date = new Date(timestamp.seconds * 1000);
  //   const options =
  //     region === 'US'
  //       ? { timeZone: 'America/Chicago', hour12: true }
  //       : { timeZone: 'Europe/Stockholm', hour12: false };
  //   return date.toLocaleString(region === 'US' ? 'en-US' : 'sv-SE', options);
  // };

  const formatPriceUS = (rawPrice) => {
    const priceStr = String(rawPrice);
    // If pattern looks like 12.345 (3 decimals), convert to 12,345
    if (/^\d+(\.\d{3})$/.test(priceStr)) {
      return priceStr.replace('.', ',');
    }
    return priceStr;
  };

  const formatPriceSEK = (rawPrice) => String(rawPrice).replace('.', ' ');

  return (
    <div className="home-page">
      <h1 className="home-title">Home Gym Equipment &amp; Fitness Essentials</h1>
      <p className="quote">Build your own workout space with quality gear.</p>

      {/* Timestamp section kept as a comment
      {firstCardWithTimestamp?.timestamp && (
        <p>
          <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>Updated on:</span>{' '}
          <span style={{ color: '#34495e' }}>
            {formatTimestamp(firstCardWithTimestamp.timestamp)}{' '}
            {region === 'US' ? 'CST' : 'CET'}
          </span>
        </p>
      )}
      */}

      {/* card0 remains always visible with both text and text1 (currently disabled) */}
      {card0 && false && (
        <Card className="mb-3 custom-card h-100">
          <Row className="g-0 align-items-stretch h-100">
            <Col md={8} className="h-100 d-flex flex-column">
              <Card.Body className="flex-grow-1 d-flex flex-column">
                <Card.Title className="card-title">{card0.title}</Card.Title>
                <Card.Text>{card0.text}</Card.Text>
                <Card.Text>{card0.text1}</Card.Text>
              </Card.Body>
            </Col>
            <Col md={4} className="h-100%">
              <Card.Img
                src={card0.image || '/assets/images/homegym.png'}
                alt={card0.title || 'Card0 Image'}
                className="card-img"
                style={{ width: '30%', height: 'auto', margin: '10px' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <div className="cards-section container">
        <Row className="mb-4 justify-content-center g-3">
          {filteredCards.length > 0 ? (
            filteredCards
              .filter((card) => (region === 'US' ? card.text1 : card.text))
              .map((card) => (
                <Col key={card.id} xs={12} sm={6} lg={3} className="d-flex">
                  <Card className="basic-card">
                    <a
                      href={card.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleImageClick(card)}
                    >
                      <Card.Img
                        className="img-size"
                        variant="top"
                        src={card.image}
                        alt={card.title}
                        loading="lazy"
                      />
                    </a>
                    <Card.Body className="d-flex flex-column">
                      {region === 'US' ? (
                        <Card.Text className="product-text">{card.text1}</Card.Text>
                      ) : (
                        <Card.Text className="product-text">{card.text}</Card.Text>
                      )}
                      {(card.rating || card.review) && (
                        <div className="rating-reviews">
                          {card.rating && (
                            <span className="rating-text">Rating: {card.rating} / 5</span>
                          )}
                          {card.review && (
                            <span className="reviews-text">Reviews: {card.review}</span>
                          )}
                        </div>
                      )}
                      <Card.Text className="price-text notranslate">
                        {region === 'US'
                          ? `$${formatPriceUS(card.price)}`
                          : `SEK ${formatPriceSEK(card.price)}`}
                      </Card.Text>
                      <a
                        href={card.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleButtonClick(card)}
                      >
                        <Button variant="primary" className="large-button">
                          View Product
                        </Button>
                      </a>
                    </Card.Body>
                  </Card>
                </Col>
              ))
          ) : (
            <p>Welcome to Pure Health Nexus...</p>
          )}
        </Row>
      </div>
    </div>
  );
}

export default HomeGymPage;
